/**
 * What each role allows: the roles as badges with one sentence each, collapsed behind a toggle
 * unless always shown, and a link to the help page on roles.
 */
import { useId, useState } from 'react';
import { Badge, type BadgeProps, Button, Icon, getSingleRoleBadgeColor } from '@insio/ui-toolkit';
import { helpPageUrl } from '@/config';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** The help page that explains the roles, as its path under the help root. */
export const ROLES_HELP_PATH = 'account/roles/';

interface RolesGuideProps<TRole extends string> {
  /** The roles to explain, in display order. */
  roles: readonly TRole[];
  label: (role: TRole) => string;
  description: (role: TRole) => string;
  /** Badge colour of a role; account roles' own colours by default. */
  badgeColor?: (role: TRole) => BadgeProps['color'];
  /** False shows the list outright, with no toggle; true by default. */
  isCollapsible?: boolean;
  testId?: string;
}

export function RolesGuide<TRole extends string>({
  roles,
  label,
  description,
  badgeColor = getSingleRoleBadgeColor,
  isCollapsible = true,
  testId = 'roles-guide'
}: RolesGuideProps<TRole>) {
  const t = useFeatureTranslation('shared.rolesGuide');
  const [isToggledOpen, setOpen] = useState(false);
  const open = !isCollapsible || isToggledOpen;
  const contentId = useId();

  return (
    <div className='flex flex-col gap-sm' data-testid={testId}>
      {isCollapsible && (
        <Button
          variant='ghost'
          size='sm'
          className='self-start -ml-sm px-sm'
          icon={<Icon name='chevron-down' rotate={open ? 180 : 0} />}
          iconPosition='right'
          aria-expanded={open}
          aria-controls={open ? contentId : undefined}
          onClick={() => setOpen(value => !value)}
          testId={`${testId}-toggle`}
        >
          {t('toggle')}
        </Button>
      )}

      {open && (
        <div id={contentId} className='flex flex-col gap-sm'>
          <dl className='flex flex-col gap-sm sm:grid sm:grid-cols-[max-content_1fr] sm:items-start sm:gap-x-md sm:gap-y-sm'>
            {roles.map(role => (
              <div
                key={role}
                className='flex flex-col items-start gap-xs sm:contents'
                data-testid={`${testId}-role-${role}`}
              >
                <dt>
                  <Badge size='sm' color={badgeColor(role)}>
                    {label(role)}
                  </Badge>
                </dt>
                <dd className='m-0 text-sm text-foreground-muted'>{description(role)}</dd>
              </div>
            ))}
          </dl>
          <a
            href={helpPageUrl(ROLES_HELP_PATH)}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t('helpOpensInNewTab')}
            className='inline-flex items-center gap-xs self-start text-sm'
            data-testid={`${testId}-help`}
          >
            {t('help')}
            <Icon name='arrow-up-right-from-square' size='sm' aria-hidden='true' />
          </a>
        </div>
      )}
    </div>
  );
}
